import type { FC, ReactNode } from 'react'
import type { PostSummary } from '@/lib/cms'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight, Clock } from 'lucide-react'
import { revealDelay } from '@/lib/motion'

interface PostCardProps {
    post: PostSummary
    index?: number
    large?: boolean
}

export const PostCard: FC<PostCardProps> = ({ post, index = 0, large = false }): ReactNode => {
    return (
        <article
            className='group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg'
            data-reveal
            style={revealDelay((index % 3) * 100)}
        >
            <div className={`relative overflow-hidden bg-muted ${large ? 'aspect-[16/9]' : 'aspect-[16/10]'}`}>
                {post.cover_image && (
                    <Image
                        src={post.cover_image}
                        alt={post.cover_image_alt ?? ''}
                        fill
                        sizes={large ? '(min-width: 1024px) 60vw, 100vw' : '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'}
                        className='reveal-settle object-cover transition-transform duration-700 group-hover:scale-105'
                    />
                )}
            </div>

            <div className='flex flex-1 flex-col p-6'>
                <p className='flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-muted-foreground'>
                    {post.topic_name && (
                        <span className='rounded-full bg-secondary px-3 py-1 text-navy' data-mdlfy-cms={post.topic_id ? `blog_topics:${post.topic_id}:name` : undefined}>{post.topic_name}</span>
                    )}

                    {post.read_minutes && (
                        <span className='inline-flex items-center gap-1.5'>
                            <Clock className='size-4' aria-hidden='true' />
                            <span data-mdlfy-cms={`blog_posts:${post.id}:read_minutes`}>{post.read_minutes}</span> minute read
                        </span>
                    )}
                </p>

                <h3 className={`mt-3 font-bold leading-tight ${large ? 'text-3xl' : 'text-2xl'}`}>
                    <Link href={`/blog/${post.slug}/`} className='after:absolute after:inset-0 hover:text-primary focus-visible:text-primary'>
                        <span data-mdlfy-cms={`blog_posts:${post.id}:title`}>{post.title}</span>
                    </Link>
                </h3>

                {post.excerpt && (
                    <p className='mt-3 flex-1 text-base' data-mdlfy-cms={`blog_posts:${post.id}:excerpt`}>{post.excerpt}</p>
                )}

                <span className='mt-4 inline-flex items-center gap-2 font-bold text-primary' aria-hidden='true'>
                    Read the guide
                    <ArrowRight className='size-5 transition-transform group-hover:translate-x-1' />
                </span>
            </div>
        </article>
    )
}

interface PostCardsProps {
    posts: PostSummary[]
    title?: string
    intro?: string
    showAllLink?: boolean
    tone?: 'plain' | 'tinted'
}

const PostCards: FC<PostCardsProps> = ({ posts = [], title = "Helpful guides", intro, showAllLink = true, tone = 'plain' }): ReactNode => {
    if (!posts.length) return null

    return (
        <section aria-labelledby='post-cards-heading' className={tone === 'tinted' ? 'bg-sand' : ''}>
            <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8'>
                <div className='flex flex-wrap items-end justify-between gap-6' data-reveal>
                    <div className='max-w-2xl'>
                        <h2 id='post-cards-heading' className='text-3xl font-bold sm:text-4xl'>
                            {title}
                        </h2>

                        {intro && (
                            <p className='mt-3 text-lg text-muted-foreground'>
                                {intro}
                            </p>
                        )}
                    </div>

                    {showAllLink && (
                        <Link href='/blog/' className='inline-flex items-center gap-2 text-lg font-bold text-primary underline underline-offset-4'>
                            All articles
                            <ArrowRight className='size-5' aria-hidden='true' />
                        </Link>
                    )}
                </div>

                <div className='mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                    {posts.map((post, index) => (
                        <div key={post.id} className='relative'>
                            <PostCard post={post} index={index} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default PostCards
